import { FaPowerOff, FaChevronLeft } from "react-icons/fa6";
import { useGo, useLogout, useParsed, useTranslation, useGetIdentity, useBack } from "@refinedev/core";
import { ReactNode, cloneElement, useEffect, useRef } from "react";
import { IAuthUser } from "@auth/firebase/interfaces";
import { CommonTemplate } from "./common";
import { throttle } from "lodash-es";
import { useAtom } from "jotai";
import { drawerAtom } from "./state";
import { FaUserCircle } from "react-icons/fa";
import { useAuthorizedMenu } from "hooks/useAuthorizedMenu";
// import { useGoogleAccessToken } from "hooks/google/useGoogleAccessToken";

export interface DefaultTemplateProps {
    navbar?: JSX.Element
    tabbar?: JSX.Element
    sidebar?: JSX.Element
    navbarProps?: Record<string, any>
    children?: ReactNode
    /**
     * Content of the Navbar's "left" area
     */
    left?: string | number | React.ReactNode;
    /**
     * Content of the Navbar's "title" area
     */
    title?: string | number | React.ReactNode;
    /**
     * Content of the Navbar's "subtitle" area
     */
    subtitle?: string | number | React.ReactNode;
    /**
     * Content of the Navbar's "right" area
     */
    right?: string | number | React.ReactNode;
    /**
     * Content of the Navbar's "subnavbar" area
     */
    subnavbar?: string | number | React.ReactNode;
    /**
    * Content of the Navbar's "subnavbar" area
    */
    actions?: string | number | React.ReactNode;
    /**
    * Content of the Navbar's "subnavbar" area
    */
    sideaction?: string | number | React.ReactNode;
    /**
    * Scroll of the main area, by default true
    */
    fixed?: boolean;

    /**
    * Content of the Navbar's "center" area
    */
    center?: string | number | React.ReactNode;
}

export function DefaultTemplate({
    children,
    navbar,
    tabbar,
    sidebar,
    navbarProps,
    subnavbar,
    actions,
    left,
    right,
    title,
    subtitle,
    sideaction,
    center,
    fixed = false,
}: DefaultTemplateProps) {
    const go = useGo();
    const { mutate: logout } = useLogout();
    // const { data: isAuthenticated } = useIsAuthenticated()
    const parsed = useParsed();
    // logger.debug(parsed)
    // logger.debug(isAuthenticated)
    const { translate } = useTranslation();
    const headerRef = useRef<HTMLHeadElement>(null)
    const footerRef = useRef<HTMLDivElement>(null)
    const mainRef = useRef<HTMLDivElement>(null)

    const { data: identity } = useGetIdentity<IAuthUser>();

    const back = useBack()
    const [drawer, setDrawer] = useAtom(drawerAtom)

    // get google access token
    // useGoogleAccessToken()

    const { menuItems } = useAuthorizedMenu()
    // logger.debug(menuItems)

    const { className: classNameNavbar, ...restNavbarProps } = navbarProps ?? {}

    if (!left)
        left = !menuItems.map(l => l.list).includes(parsed.pathname!) ?
            <button
                className="btn btn-ghost btn-sm"
                aria-label={translate("page.appointments.actions.goBack")}

                onClick={back}
            ><FaChevronLeft className="w-4 h-4" /></button> : <></>


    useEffect(() => {
        /* When the user scrolls down, hide the navbar. When the user scrolls up, show the navbar */
        var prevScrollpos = window.pageYOffset;;
        window.onscroll = throttle(function () {
            var currentScrollPos = window.pageYOffset;;
            // logger.debug(currentScrollPos)
            var offset = window.scrollMaxY || (document.documentElement.scrollHeight - document.documentElement.clientHeight)
            // document.getElementsByClassName('navbar-end')[0].innerHTML = `${currentScrollPos} - ${offset}`
            if (currentScrollPos > 0 && currentScrollPos < offset) {
                if (prevScrollpos > currentScrollPos) {
                    headerRef.current!.style.top = "0";
                } else {
                    headerRef.current!.style.top = `-${headerRef.current!.clientHeight}px`
                }
                mainRef.current!.style.marginBottom = `${footerRef.current?.clientHeight ?? 0}px`
            }
            // logger.debug(prevScrollpos, currentScrollPos)
            prevScrollpos = currentScrollPos;
        }, 200)

        const updateFooterMargin = throttle(() => {
            if (mainRef.current && footerRef.current) {
                mainRef.current.style.marginBottom = `${footerRef.current.clientHeight}px`;
            }
        }, 500)

        // Set initial margin
        updateFooterMargin();

        // Add event listener to handle resizing
        window.addEventListener('resize', updateFooterMargin);

        return () => {
            window.onscroll = null;
            window.removeEventListener('resize', updateFooterMargin);
        }
    }, [])

    return <CommonTemplate>
        <div className="h-full flex flex-col md:flex-row drawer drawer-open bg-base-100">
            {sidebar ?? <aside className='relative p-4 flex-col h-screen hidden md:flex items-stretch w-1/4 lg:w-1/5 max-w-[250px] bg-secondary text-primary-content'>
                <div className="z-30 absolute top-0 -mr-20 right-0 w-20 h-8 bg-transparent rounded-tl-full shadow-[-2rem_0_var(--color-secondary)]"></div>
                <div className="z-30 absolute bottom-0 -mr-20 right-0 w-20 h-8 bg-transparent rounded-bl-full shadow-[-2rem_0_var(--color-secondary)]"></div>

                <div className='flex flex-col items-center gap-3 my-4'>
                    <figure>
                        <img src="/hachimind.svg" alt="hachimind logo" className="w-40" />
                    </figure>
                </div>
                <ul
                    role="navigation"
                    aria-label="Main navigation"
                    className="relative -right-4 mt-2 items-start grow menu p-0 pt-10 overflow-auto scrollbar-thin w-full"
                >
                    {menuItems.map(({ name, list, meta }) => {
                        const { icon, label, ...rest } = meta!
                        const isActive = parsed.pathname === list;
                        return <li key={name} className='w-full my-2 relative'>
                            {isActive && <div className="absolute top-0 -mt-20 right-0 w-8 h-20 bg-transparent rounded-br-full shadow-[0_2rem_var(--color-base-100)]"></div>}
                            <a
                                className={`flex gap-4 items-center p-4 transition-colors duration-200 rounded-l-full ${isActive
                                    ? 'bg-base-100 text-secondary font-semibold'
                                    : 'text-secondary-content/70 hover:bg-white/10 rounded-lg'
                                    }`}
                                onClick={() => go({ to: list! as string })}
                                aria-current={isActive ? 'page' : undefined}
                            // {...rest}
                            >
                                {name != 'config' ?
                                    (icon ? cloneElement(icon as React.ReactElement, { className: 'w-6 h-6' }) : null) :
                                    <div className="avatar">
                                        <object data={identity?.photoURL!} type="image/jpeg" className="w-8 h-8 rounded-full">
                                            <FaUserCircle className="w-8 h-8" />
                                        </object>
                                    </div>
                                }
                                <span className="z-30 capitalize text-lg">{translate(label ?? name)}</span>
                            </a>
                            {isActive && <div className="absolute bottom-0 -mb-20 right-0 w-8 h-20 bg-transparent rounded-tr-full shadow-[0_-2rem_var(--color-base-100)]"></div>}
                        </li>
                    }
                    )}
                </ul>
                <div className="mt-4 z-30">
                    {
                        // isAuthenticated?.authenticated ?
                        <button className='btn btn-ghost w-full text-base-100 hover:bg-white/10' onClick={() => logout()} aria-label="Log out"><FaPowerOff />Logout</button>
                        // :
                        // <button className='btn btn-ghost' onClick={() => go({ to: '/login' })}><Power />Login</button>
                    }
                </div>
            </aside>}
            <div className='flex-1 h-full flex flex-col relative drawer drawer-end'>
                <header ref={headerRef} className="bg-base-100 pl-4 z-20 sticky top-0 md:w-full transition-all duration-300">
                    {navbar ?? <nav className={`navbar py-4 min-h-12 ${classNameNavbar ?? ''}`} {...restNavbarProps}>
                        <div className="navbar-start">
                            {left}
                            <span className="font-bold text-xl">{title ?? translate(parsed.resource?.meta?.label ?? parsed.resource?.name ?? parsed.pathname!)}</span>
                            {subtitle ? <span className="font-light">{subtitle}</span> : <></>}
                        </div>
                        <div className="navbar-center flex flex-col leading-none">
                            {center}
                        </div>
                        <div className="navbar-end gap-2">
                            {right}
                        </div>
                    </nav>}
                    {subnavbar ? <nav className="navbar pt-0 min-h-0 gap-2">
                        {subnavbar}
                    </nav> : <></>}
                </header>
                <main ref={mainRef} className={`w-full flex-1 flex flex-col transition-all duration-300 drawer-content lg:pl-8 ${fixed ? 'overflow-hidden' : 'scrollbar-thin overflow-y-auto'}`}>
                    {children}
                </main>
                <input type="checkbox" className="drawer-toggle" checked={drawer} onChange={() => null} />
                <div className="drawer-side z-30">
                    {sideaction}
                </div>
                <div className="fixed bottom-0 right-0 mr-5 mb-20">
                    {actions}
                </div>
                {tabbar ?? <footer ref={footerRef} role="navigation" aria-label="Mobile navigation" className="dock md:hidden bg-base-100 border-t border-base-300">
                    {menuItems.map(({ name, list, meta }) => {
                        const { icon, label, ...rest } = meta!
                        const isActive = parsed.pathname === list;
                        return <button
                            key={name}
                            className={`flex flex-col items-center justify-center pt-2 pb-1 gap-1 w-full transition-colors duration-200 ${isActive ? 'text-secondary' : 'text-base-content/60'}`}
                            onClick={() => go({ to: list as string })}
                            aria-label={translate(label ?? name)}
                            aria-current={isActive ? 'page' : undefined}
                        // {...rest}
                        >
                            <div className={`flex items-center justify-center h-8 w-16 py-2 rounded-full transition-colors duration-200 ${isActive ? 'bg-primary text-primary-content' : 'hover:bg-base-200'}`}>
                                {name != 'config' ?
                                    (icon ? cloneElement(icon as React.ReactElement, { className: 'w-6 h-6' }) : null) :
                                    <div className="avatar">
                                        <object data={identity?.photoURL!} type="image/jpeg" className="w-6 rounded-full">
                                            <FaUserCircle className="w-6 h-6" />
                                        </object>
                                    </div>
                                }
                            </div>
                            <span className={`text-xs ${isActive ? 'font-bold' : ''}`}>{translate(label ?? name)}</span>
                        </button>
                    })}
                </footer>}
            </div>
        </div>
    </CommonTemplate>;
}
